import { useMemo, useState } from 'react'
import {
  HeartPulse,
  Gauge,
  Thermometer,
  Activity,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Lock,
  ChevronDown,
  Info,
} from 'lucide-react'
import { apiGet, patchJson } from '../lib/api.js'
import { useChartSave } from '../lib/chartSave.jsx'
import { asList } from '../lib/chart-helpers.js'
import {
  Badge, Button, ConfirmDialog, EmptyState, ErrorState, Field, IconButton, Input,
  fmtDateTime, useToast,
} from '../kit'

/**
 * Vital Signs — part of the Clinical section.
 *
 * Stored on `chart.hx[0].vital_sign[]` (the schema nests vital_sign inside hx):
 *   { SBP, DBP, HR, BT, created_at, created_by, modified_at, modified_by }
 *
 * A reading can be added many times and each can be edited / deleted. The card
 * shows the LATEST reading prominently (by created_at) and keeps the full log
 * in a collapsible history list. Writes consolidate all readings onto hx[0],
 * preserving every other hx field — ClinicalEditor's save mirrors this so the
 * two never clobber each other.
 */
export default function VitalSignsEditor({ chart, canEdit, lockNote, me, onSaved }) {
  const chartId = chart?.id
  const uid = me?.xano_id || 0

  // Read the vital_sign log from the first hx entry that carries one.
  const readings = useMemo(() => {
    const hx = Array.isArray(chart?.hx) ? chart.hx : []
    for (const e of hx) {
      if (Array.isArray(e?.vital_sign) && e.vital_sign.length) return e.vital_sign
    }
    return Array.isArray(hx[0]?.vital_sign) ? hx[0].vital_sign : []
  }, [chart])

  // Latest = the reading with the greatest created_at (fallback: last in array).
  const latestIdx = useMemo(() => {
    if (readings.length === 0) return -1
    let best = 0
    for (let i = 1; i < readings.length; i++) {
      if ((readings[i]?.created_at || 0) >= (readings[best]?.created_at || 0)) best = i
    }
    return best
  }, [readings])

  const [saving, setSaving]   = useState(false)
  const [err, setErr]         = useState(null)
  const [formOpen, setForm]   = useState(false)
  const [editIdx, setEditIdx] = useState(null) // null = adding a new reading
  const [draft, setDraft]     = useState(blankForm())
  const [baseline, setBaseline] = useState(blankForm()) // what the form opened with
  const [showHistory, setShowHistory] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(null)
  // While the delete request runs the dialog stays open (busy) showing the text it
  // was confirmed with — `readings` may refresh underneath it.
  const [deletingText, setDeletingText] = useState(null)
  const toast = useToast()

  function openAdd() {
    setEditIdx(null); setDraft(blankForm()); setBaseline(blankForm()); setErr(null); setForm(true)
  }
  function openEdit(i) {
    const r = readings[i] || {}
    const init = { SBP: numStr(r.SBP), DBP: numStr(r.DBP), HR: numStr(r.HR), BT: numStr(r.BT) }
    setEditIdx(i)
    setDraft(init); setBaseline(init)
    setErr(null); setForm(true)
  }
  function closeForm() { setForm(false); setEditIdx(null); setErr(null) }

  // Rebuild the full hx, dropping vital_sign off every entry, then attach the
  // new log to hx[0]. Creates a host entry if the chart has none yet.
  function buildHxWith(nextReadings, baseHx) {
    let hx = (Array.isArray(baseHx) ? baseHx : []).map((e) => {
      const { vital_sign, ...rest } = e || {}
      return rest
    })
    if (hx.length === 0) {
      hx = [{ PE: '', note: '', canvas: '', tag: ['Full Mouth'], tag_type: 'Full Mouth' }]
    }
    hx[0] = { ...hx[0], vital_sign: nextReadings }
    return hx
  }

  // The hx to write onto, re-read from the server. The save bar saves Clinical
  // first and then this section; Clinical writes the same `hx` array, and our
  // `chart` prop is still the pre-save copy at that moment — building on it
  // would silently wipe the clinical notes just saved. Falls back to the prop.
  async function freshHx() {
    const pid = chart?.patient?.id ?? chart?.patient ?? chart?.patient_id
    const tid = chart?.treatment?.id ?? chart?.treatment ?? chart?.treatment_id
    if (pid == null || tid == null) return chart?.hx
    try {
      const list = await apiGet('/api/charts', { patient_id: pid, treatment_id: tid })
      const hit = asList(list).find((c) => String(c?.id) === String(chartId))
      return hit && Array.isArray(hit.hx) ? hit.hx : chart?.hx
    } catch { return chart?.hx }
  }

  async function persist(nextReadings) {
    if (!chartId) { setErr('ยังไม่มี chart ในเคสนี้'); return }
    setSaving(true); setErr(null)
    try {
      await patchJson(`/api/charts/${chartId}`, { hx: buildHxWith(nextReadings, await freshHx()) })
      onSaved?.()
    } catch (e) {
      const msg = e?.message || 'บันทึกไม่สำเร็จ'
      setErr(msg)
      toast.error('บันทึกไม่สำเร็จ', { detail: msg })
      throw e
    } finally {
      setSaving(false)
    }
  }

  // Returns true once saved (false on a validation or save error) so the
  // inline button and the page's บันทึกทั้งหมด bar can confirm with a toast.
  async function submitForm() {
    const v = parseForm(draft)
    if (v == null) { setErr('กรอกอย่างน้อย 1 ค่า'); toast.error('กรอกอย่างน้อย 1 ค่า'); return false }
    const now = Date.now()
    let next
    if (editIdx == null) {
      next = [...readings, { ...v, created_at: now, created_by: uid }]
    } else {
      next = readings.map((r, i) =>
        i === editIdx ? { ...r, ...v, modified_at: now, modified_by: uid } : r,
      )
    }
    try { await persist(next); closeForm(); return true } catch { return false /* err shown in card */ }
  }
  async function submitAndConfirm() {
    if (await submitForm()) toast.success('บันทึกแล้ว')
  }

  async function doDelete(i) {
    const next = readings.filter((_, j) => j !== i)
    try { await persist(next); toast.success('ลบรายการวัดแล้ว') } catch { /* err shown in card */ }
  }

  // An open form with typed changes is "unsaved" for the page: it shows in the
  // บันทึกทั้งหมด bar and blocks ChartVisitNav, like Clinical / Oral Exam.
  const dirty = formOpen && JSON.stringify(draft) !== JSON.stringify(baseline)
  useChartSave('vital-signs', dirty, submitForm, closeForm)

  const latest = latestIdx >= 0 ? readings[latestIdx] : null

  return (
    <section className="bg-white border border-slate-200 rounded-2xl">
      <header className="px-5 pt-4 pb-3 flex items-center gap-2.5 border-b border-slate-100">
        <span className="w-7 h-7 rounded-lg bg-slate-50 text-slate-500 flex items-center justify-center ring-1 ring-slate-100">
          <HeartPulse size={14} strokeWidth={2} />
        </span>
        <div className="flex-1">
          <div className="text-[12.5px] uppercase tracking-[0.14em] font-semibold text-slate-500">Vital Signs</div>
          <div className="text-[13.5px] text-slate-400 -mt-0.5">สัญญาณชีพ · แสดงค่าล่าสุด</div>
        </div>
        {saving && <Loader2 size={14} className="animate-spin text-slate-400 mr-1" />}
        {canEdit && chartId && (
          <Button variant="soft" size="sm" icon={Plus} onClick={openAdd}>เพิ่ม Vital Sign</Button>
        )}
        {!canEdit && chartId && lockNote && (
          <Badge tone="amber" title={lockNote}><Lock size={11} strokeWidth={2.25} />ล็อก</Badge>
        )}
      </header>

      <div className="p-4 sm:p-5 space-y-4">
        {err && <ErrorState title={err} />}

        {!chartId && <EmptyState title="ยังไม่มี chart ในเคสนี้" />}

        {/* Add / edit form */}
        {formOpen && (
          <VitalForm
            draft={draft}
            setDraft={setDraft}
            isEdit={editIdx != null}
            saving={saving}
            onSubmit={submitAndConfirm}
            onCancel={closeForm}
          />
        )}

        {chartId && readings.length === 0 && !formOpen && (
          <EmptyState
            icon={Activity}
            title="ยังไม่มีการบันทึกสัญญาณชีพ"
            hint={canEdit ? 'กดปุ่ม "เพิ่ม Vital Sign" เพื่อเริ่มบันทึก' : undefined}
          />
        )}

        {/* Latest reading — prominent */}
        {latest && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[12px] uppercase tracking-[0.14em] font-semibold text-slate-400">ค่าล่าสุด</div>
              <VitalMetaPopup reading={latest} />
            </div>
            <div className="grid grid-cols-3 gap-2.5">
              <StatTile icon={<Gauge size={14} strokeWidth={2} />} label="BP" value={bpText(latest)} unit="mmHg" tone="rose" />
              <StatTile icon={<HeartPulse size={14} strokeWidth={2} />} label="HR" value={numText(latest.HR)} unit="bpm" tone="violet" />
              <StatTile icon={<Thermometer size={14} strokeWidth={2} />} label="BT" value={numText(latest.BT)} unit="°C" tone="amber" />
            </div>
          </div>
        )}

        {/* History */}
        {readings.length > 1 && (
          <div>
            <button
              type="button"
              onClick={() => setShowHistory((v) => !v)}
              className="inline-flex items-center gap-1.5 text-[13px] font-medium text-slate-500 hover:text-brand-700 transition-colors"
            >
              <ChevronDown size={13} strokeWidth={2.25} className={`transition-transform ${showHistory ? 'rotate-180' : ''}`} />
              ประวัติการวัด ({readings.length})
            </button>
            {showHistory && (
              <div className="mt-2 rounded-xl bg-slate-50/50 ring-1 ring-slate-100 divide-y divide-slate-100">
                {readings
                  .map((r, i) => ({ r, i }))
                  .sort((a, b) => (b.r?.created_at || 0) - (a.r?.created_at || 0))
                  .map(({ r, i }) => (
                    <VitalRow
                      key={i}
                      reading={r}
                      isLatest={i === latestIdx}
                      canEdit={canEdit}
                      busy={saving}
                      onEdit={() => openEdit(i)}
                      onDelete={() => setConfirmDelete(i)}
                    />
                  ))}
              </div>
            )}
          </div>
        )}
      </div>

      {(confirmDelete != null || deletingText) && (
        <ConfirmDialog
          title="ลบรายการวัดนี้?"
          message={deletingText || deleteReadingText(readings[confirmDelete])}
          confirmLabel="ลบ"
          tone="danger"
          busy={!!deletingText}
          onConfirm={async () => {
            if (deletingText) return
            const idx = confirmDelete
            setDeletingText(deleteReadingText(readings[idx]))
            try { await doDelete(idx) } finally { setDeletingText(null); setConfirmDelete(null) }
          }}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </section>
  )
}

function deleteReadingText(r) {
  return `ค่าสัญญาณชีพ BP ${bpText(r)} · HR ${numText(r?.HR)} · BT ${numText(r?.BT)} (${fmtDateTime(r?.created_at)}) จะถูกลบออกจาก chart อย่างถาวร`
}

/* -------------------------------------------------------------------------- */
/* Add / edit form                                                            */
/* -------------------------------------------------------------------------- */

function VitalForm({ draft, setDraft, isEdit, saving, onSubmit, onCancel }) {
  return (
    <div className="rounded-xl ring-1 ring-brand-200 bg-brand-50/40 p-4">
      <div className="text-[13px] font-semibold text-brand-800 mb-3">
        {isEdit ? 'แก้ไขรายการวัด' : 'บันทึกสัญญาณชีพใหม่'}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <NumField label="SBP" unit="mmHg" value={draft.SBP} onChange={(v) => setDraft({ ...draft, SBP: v })} placeholder="120" />
        <NumField label="DBP" unit="mmHg" value={draft.DBP} onChange={(v) => setDraft({ ...draft, DBP: v })} placeholder="80" />
        <NumField label="HR" unit="bpm" value={draft.HR} onChange={(v) => setDraft({ ...draft, HR: v })} placeholder="72" />
        <NumField label="BT" unit="°C" value={draft.BT} onChange={(v) => setDraft({ ...draft, BT: v })} placeholder="36.5" step="0.1" />
      </div>
      <div className="flex items-center justify-end gap-2 mt-4">
        <Button variant="secondary" size="sm" onClick={onCancel} disabled={saving}>ยกเลิก</Button>
        <Button size="sm" onClick={onSubmit} loading={saving}>บันทึก</Button>
      </div>
    </div>
  )
}

function NumField({ label, unit, value, onChange, placeholder, step }) {
  return (
    <Field label={<>{label} <span className="text-slate-400 font-normal">{unit}</span></>}>
      <Input
        type="number"
        inputMode="decimal"
        step={step || '1'}
        min="0"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="tabular-nums"
      />
    </Field>
  )
}

/* -------------------------------------------------------------------------- */
/* Latest tiles + history row                                                 */
/* -------------------------------------------------------------------------- */

const TILE_TONE = {
  rose:   { bg: 'bg-rose-50',   ring: 'ring-rose-100',   icon: 'text-rose-500' },
  violet: { bg: 'bg-violet-50', ring: 'ring-violet-100', icon: 'text-violet-500' },
  amber:  { bg: 'bg-amber-50',  ring: 'ring-amber-100',  icon: 'text-amber-500' },
}

function StatTile({ icon, label, value, unit, tone }) {
  const t = TILE_TONE[tone] || TILE_TONE.rose
  return (
    <div className={`rounded-xl ${t.bg} ring-1 ${t.ring} px-3 py-2.5`}>
      <div className="flex items-center gap-1.5 mb-1">
        <span className={t.icon}>{icon}</span>
        <span className="text-[11.5px] uppercase tracking-[0.1em] font-semibold text-slate-500">{label}</span>
      </div>
      <div className="flex items-baseline gap-1">
        <span className="text-[20px] font-semibold text-slate-800 tabular-nums leading-none">{value}</span>
        <span className="text-[12px] text-slate-400">{unit}</span>
      </div>
    </div>
  )
}

function VitalRow({ reading, isLatest, canEdit, busy, onEdit, onDelete }) {
  return (
    <div className="px-3.5 py-2.5 flex items-center gap-3 flex-wrap hover:bg-white transition-colors first:rounded-t-xl last:rounded-b-xl">
      <div className="flex items-center gap-4 flex-1 min-w-0 text-[13.5px] tabular-nums">
        <Metric label="BP" value={bpText(reading)} />
        <Metric label="HR" value={numText(reading.HR)} />
        <Metric label="BT" value={numText(reading.BT)} />
      </div>
      {isLatest && (
        <Badge tone="emerald">ล่าสุด</Badge>
      )}
      <span className="text-[12px] text-slate-400 font-mono">{fmtDateTime(reading.modified_at || reading.created_at)}</span>
      {canEdit && (
        <div className="flex items-center gap-0.5">
          <IconButton icon={Pencil} size="sm" onClick={onEdit} disabled={busy} label="แก้ไข" />
          <IconButton icon={Trash2} size="sm" variant="danger-soft" onClick={onDelete} disabled={busy} label="ลบ" />
        </div>
      )}
    </div>
  )
}

function Metric({ label, value }) {
  return (
    <span className="inline-flex items-baseline gap-1">
      <span className="text-[11px] uppercase tracking-[0.08em] font-semibold text-slate-400">{label}</span>
      <span className="text-slate-700 font-medium">{value}</span>
    </span>
  )
}

/* -------------------------------------------------------------------------- */
/* Meta popup — mirrors the consent / appointment info popups                 */
/* -------------------------------------------------------------------------- */

function VitalMetaPopup({ reading }) {
  const [open, setOpen] = useState(false)
  if (!reading) return null
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="meta info"
        className={`inline-flex items-center justify-center w-6 h-6 rounded-full transition-all ${
          open ? 'text-brand-700 bg-brand-50 ring-1 ring-brand-200' : 'text-slate-400 hover:text-slate-700 hover:bg-slate-100 ring-1 ring-transparent'
        }`}
      >
        <Info size={13} strokeWidth={2} />
      </button>
      {open && (
        <div role="tooltip" className="absolute right-0 bottom-full mb-2 z-20 min-w-[168px] rounded-lg bg-white ring-1 ring-brand-200 shadow-lg shadow-brand-900/10 overflow-hidden pointer-events-none animate-ss-pop-in">
          <MetaRow label="Recorded">
            <span className="font-mono">{fmtDateTime(reading.created_at)}</span>
          </MetaRow>
          <MetaRow label="Modified" last>
            {reading.modified_at
              ? <span className="font-mono">{fmtDateTime(reading.modified_at)}</span>
              : <span className="italic text-slate-400">ยังไม่ถูกแก้ไข</span>}
          </MetaRow>
        </div>
      )}
    </div>
  )
}

function MetaRow({ label, children, last }) {
  return (
    <div className={`px-3 py-1.5 ${last ? '' : 'border-b border-brand-100'}`}>
      <div className="text-[11.5px] uppercase tracking-[0.1em] font-semibold text-brand-600">{label}</div>
      <div className="text-[12.5px] text-slate-700 leading-snug">{children}</div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function blankForm() {
  return { SBP: '', DBP: '', HR: '', BT: '' }
}

function numStr(v) {
  return v == null || v === '' ? '' : String(v)
}

// Parse the form into a reading value object. Returns null if everything blank.
function parseForm(d) {
  const SBP = toInt(d.SBP)
  const DBP = toInt(d.DBP)
  const HR  = toInt(d.HR)
  const BT  = toFloat(d.BT)
  if (SBP == null && DBP == null && HR == null && BT == null) return null
  return { SBP, DBP, HR, BT }
}

function toInt(v) {
  if (v == null || String(v).trim() === '') return null
  const n = parseInt(v, 10)
  return Number.isFinite(n) ? n : null
}
function toFloat(v) {
  if (v == null || String(v).trim() === '') return null
  const n = parseFloat(v)
  return Number.isFinite(n) ? n : null
}

function numText(v) {
  return v == null || v === '' ? '—' : String(v)
}
function bpText(r) {
  const s = r?.SBP, d = r?.DBP
  if (s == null && d == null) return '—'
  return `${s == null ? '—' : s}/${d == null ? '—' : d}`
}
